import React, { useState, useEffect, useMemo } from "react";
import { call } from "services/axiosService/core";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useMaterialUIController } from "context";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import LockIcon from "@mui/icons-material/Lock";
import AchievementCard from "examples/Cards/ProjectCards/AchievementCard";
import GoldenUser from "assets/images/badges/GoldenUser.jpeg";
import LikedUser from "assets/images/badges/LikedUser.jpeg";
import SilverUser from "assets/images/badges/SilverUser.jpeg";
import TheBeliever from "assets/images/badges/TheBeliever.jpeg";
import TheExplorer from "assets/images/badges/TheExplorer.jpeg";
import ThePhantom from "assets/images/badges/ThePhantom.png";
import TheSuperWatcher from "assets/images/badges/TheSuperWatcher.png";
import TheWatcher from "assets/images/badges/TheWatcher.jpeg";
import BadgesData from "./bagesData";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

const logos = {
  GoldenUser,
  LikedUser,
  SilverUser,
  TheBeliever,
  TheExplorer,
  ThePhantom,
  TheSuperWatcher,
  TheWatcher,
};

function StatSummaryCard({ icon, iconBg, accentColor, label, count, sublabel }) {
  return (
    <Card
      sx={{
        borderRadius: 3,
        p: 3,
        height: "100%",
        boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
        borderLeft: `4px solid ${accentColor}`,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
        <MDBox>
          <MDTypography
            variant="caption"
            color="secondary"
            fontWeight="bold"
            sx={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
          >
            {label}
          </MDTypography>
          <MDTypography variant="h2" fontWeight="bold" lineHeight={1.1} mt={0.5}>
            {count}
          </MDTypography>
          <MDTypography variant="body2" color="secondary" mt={0.75}>
            {sublabel}
          </MDTypography>
        </MDBox>
        <MDBox
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            bgcolor: iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </MDBox>
      </MDBox>
    </Card>
  );
}

const Achievements = () => {
  const { t } = useTranslation();
  const [badges] = useState(BadgesData());
  const [progress, setProgress] = useState({});
  const [controller] = useMaterialUIController();
  const { profile } = controller;

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = () => {
    call(`users/${profile.id}/achievements/`, "GET", {})
      .then((res) => {
        if (res.status == 200) {
          setProgress(res.data.progress);
        }
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
      });
  };

  const stats = useMemo(() => {
    let earned = 0;
    let inProgress = 0;
    let locked = 0;
    badges.forEach((b) => {
      const p = progress[b.key];
      if (p === 100) earned++;
      else if (p > 0 && p < 100) inProgress++;
      else locked++;
    });
    return { earned, inProgress, locked };
  }, [badges, progress]);

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.achievements")} />
      <Grid container spacing={3} mb={3} mt={2}>
        <Grid item xs={12} md={4}>
          <StatSummaryCard
            accentColor="#00897b"
            iconBg="#e0f7f5"
            icon={<EmojiEventsIcon sx={{ color: "#00897b", fontSize: 26 }} />}
            label={t("achievements.totalAchievements")}
            count={stats.earned}
            sublabel={t("achievements.badgesEarned")}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatSummaryCard
            accentColor="#1565c0"
            iconBg="#e3f2fd"
            icon={<TrackChangesIcon sx={{ color: "#1565c0", fontSize: 26 }} />}
            label={t("achievements.inProgressLabel")}
            count={stats.inProgress}
            sublabel={t("achievements.keepGoing")}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <StatSummaryCard
            accentColor="#e65100"
            iconBg="#fff3e0"
            icon={<LockIcon sx={{ color: "#e65100", fontSize: 26 }} />}
            label={t("achievements.lockedLabel")}
            count={stats.locked}
            sublabel={t("achievements.moreToUnlock")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {badges.map((item, index) => (
          <Grid item xs={12} md={6} lg={4} key={index}>
            <AchievementCard
              logo={logos[item.logo]}
              name={item.name}
              description={item.description}
              progress={progress[item.key]}
            />
          </Grid>
        ))}
      </Grid>
    </DashboardLayout>
  );
};

export default Achievements;
